import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { QuestionInputField } from '/index';
import { ERR_TYPE_REQUIRED, FIELD_TYPE_DROPDOWN } from '/business/shared/components/questions/helper/fieldEnums';
import { dailyDistancesLookup } from '/business/shared/util/distances';

export type CommuteFactor = {
  displayName: string;
  name: string | null;
};

export type EmployeeCommuteDropdownValue = {
  displayName?: string;
  displayNameKey?: string;
  isPlaceholder?: boolean;
  name: string | null;
};

export type DistanceUnit = keyof ReturnType<typeof dailyDistancesLookup>;

const EMPLOYMENT_TYPE_VALUES: EmployeeCommuteDropdownValue[] = [
  {
    name: null,
    displayNameKey: 'business:QuestionPage.forms.commutes.selectEmploymentType',
    isPlaceholder: true,
  },
  { name: 'full_time', displayNameKey: 'business:QuestionPage.forms.commutes.employmentType.full_time' },
  { name: 'part_time', displayNameKey: 'business:QuestionPage.forms.commutes.employmentType.part_time' },
];

export default function useGetSharedEmployeeCommuteValues({
  commuterFactors,
  valueDistanceUnit,
  commuterFactorDefaultI18nKey,
  distanceDefaultI18nKey,
}: {
  commuterFactorDefaultI18nKey: string;
  commuterFactors?: CommuteFactor[];
  distanceDefaultI18nKey: string;
  valueDistanceUnit?: DistanceUnit | null;
}) {
  const { t } = useTranslation();
  const distances = dailyDistancesLookup();

  const employmentTypeField: QuestionInputField = useMemo(() => {
    return {
      propName: 'employmentType',
      element: 'employment_type',
      type: FIELD_TYPE_DROPDOWN,
      labelI18nKey: 'business:QuestionPage.forms.commutes.employmentTypeLabel',
      values: EMPLOYMENT_TYPE_VALUES,
      validation: [
        {
          rule: ERR_TYPE_REQUIRED,
          errCode: 'ERRNOEMPLOYMENTTYPE',
          errStr: t('business:Errors.transport.provideEmploymentType'),
        },
      ],
    };
  }, [t]);

  const commuterFactorValues = useMemo<EmployeeCommuteDropdownValue[]>(() => {
    const placeholder: EmployeeCommuteDropdownValue = {
      name: null,
      displayNameKey: commuterFactorDefaultI18nKey,
      isPlaceholder: true,
    };
    return [
      placeholder,
      ...(commuterFactors?.map(({ name, displayName }) => {
        return {
          name,
          displayNameKey: `business:emissionFactors.transportModes.${displayName}`,
        };
      }) ?? []),
    ];
  }, [commuterFactors, commuterFactorDefaultI18nKey]);
  const distanceUnitValues = useMemo(
    () =>
      Object.keys(distances).map((distanceTranslit) => {
        return {
          name: distanceTranslit,
          displayNameKey: `business:emissionFactors.distance.${distanceTranslit}`,
        };
      }),
    [distances],
  );
  const distanceValues = useMemo<EmployeeCommuteDropdownValue[]>(() => {
    const placeholder: EmployeeCommuteDropdownValue = {
      name: null,
      displayNameKey: distanceDefaultI18nKey,
      isPlaceholder: true,
    };
    return [
      placeholder,
      ...(valueDistanceUnit ? distances[valueDistanceUnit] : []).map(({ name, displayName }) => {
        return {
          name,
          displayName,
        };
      }),
    ];
  }, [distances, valueDistanceUnit]);
  return {
    commuterFactorValues,
    distanceUnitValues,
    distanceValues,
    distances,
    employmentTypeField,
  };
}
